import {json, random, texture} from "p3";
import common from "../common";
import definitions from "../definitions";
import Decal from "./Decal";
import Tilemap from "./Tilemap";
import * as SAT from 'sat';
import settings from "../settings";
import HiddenTreasureSpot from "./actor/misc/HiddenTreasureSpot";
import GameScene from "../scenes/GameScene";
import PlayerIvandoe from "./actor/player/PlayerIvandoe";

export default class World extends PIXI.Container {
    constructor() {
        super();

        this._camera = null;
        this._tilemap = null;

        this._tileLayer = null;
        this._gameLayer = null;
        this._shadowLayer = null;

        this._entities = [];

        this.init();
    }

    init() {
        this._camera = new P3.Camera(new PIXI.Point(common.STAGE_WIDTH * 0.5, P3.View.height * 0.5), new PIXI.Point(0, 0));

        this._tileLayer = this._addEmptyLayer(new PIXI.Point(1, 1));
        this._shadowLayer = this._addEmptyLayer(new PIXI.Point(1, 1));
        this._gameLayer = this._addEmptyLayer(new PIXI.Point(1, 1));

        this._tilemap = new Tilemap();
        this._tileLayer.view.addChild(this._tilemap);
    }

    resize() {
        this.updateCameraBounds();
    }

    update() {
        this.updateEntities();
        this.updateCamera();
        this.updateTilemap();

        this.testCollisions();
        this.sortEntities();

        this.updateCameraBounds();
    }

    updateCamera() {
        this._camera.update();
    }

    updateTilemap() {
        const width         = P3.View.width + this._tilemap.tileWidth;
        const height        = P3.View.height + this._tilemap.tileHeight;
        const zoom          = 1 / this._camera.zoom;
        const topLeft       = this._tilemap.toTile(this._camera.x - width * 0.5 * zoom, this._camera.y - height * 0.5 * zoom);
        const bottomRight   = this._tilemap.toTile(this._camera.x + width * 0.5 * zoom, this._camera.y + height * 0.5 * zoom);
        const columns       = bottomRight.x - topLeft.x;
        const rows          = bottomRight.y - topLeft.y;

        this._tilemap.updateBounds(topLeft.x, topLeft.y, columns, rows);
    }

    updateEntities() {
        for (let i = this._entities.length - 1; i >= 0; --i) {
            const entity = this._entities[i];

            if (!entity.destroyNextFrame) {
                this.cullEntity(entity);

                if (entity.alwaysUpdate || entity.visible) {
                    entity.update();
                }
            } else {
                this.removeEntity(entity, true);
            }
        }
    }

    updateCameraBounds() {
        const zoom                  = 1 / this._camera.zoom;
        this._camera.bounds.x       = P3.View.center.x * zoom;
        this._camera.bounds.y       = P3.View.center.y * zoom;
        this._camera.bounds.width   = this._tilemap.width - this._camera.bounds.x;
        this._camera.bounds.height  = this._tilemap.height - this._camera.bounds.y;
    }

    /**
     * @param {object} data
     */
    loadLevel(data) {
        const tilemapData = json(data.tilemap);
        const collisionData = json(data.collision);

        this._tilemap.load(tilemapData, collisionData.tiles);
        this.updateCameraBounds();

        this._loadEntities(tilemapData, collisionData.decals);
    }

    /**
     * @param {array<Entity>} [exclude=[]]
     */
    unloadLevel(exclude = []) {
        this.unloadTilemap();

        this.removeEntities(exclude);
    }

    unloadTilemap() {
        this._tilemap.removeAllLayers();
    }

    /**
     * @param {Entity} entity
     */
    cullEntity(entity) {
        const bounds = entity.getBounds();
        if (bounds.x === 0 && bounds.y === 0 && bounds.width === 0 && bounds.height) {
            ////console.log(entity);
        }
        entity.visible = (bounds.x + bounds.width) > 0 && bounds.x < P3.View.width && (bounds.y + bounds.height) > 0 && bounds.y < P3.View.height;
    }

    testCollisions() {
        const dynamicEntities = this._entities.filter((x) => x.collisionDynamic && x.collisionEnabled);
        const entities = this._entities.filter((x) => !x.collisionDynamic && x.collisionEnabled);

        for (let i = 0, count = dynamicEntities.length; i < count; ++i) {
            const dynamic = dynamicEntities[i];

            if (!dynamic.enabled)
                continue;

            // dynamic vs static
            for (let j = 0, count = entities.length; j < count; ++j) {
                const entity = entities[j];

                if (entity.enabled && entity.visible) {
                    switch (entity.collisionType) {
                        case definitions.COLLISION_TYPE.CIRCLE: {
                                const v = dynamic.testCollisionCircle(entity.collider);
                                /*if (dynamic.id == definitions.ENEMY_ID.FLYING_SNAKE && entity.isPellet && dynamic.targetedEnemy) {
                                    console.log('is snake AND isPellet', v, dynamic.collider, entity.collider);
                                }*/
                                if (v) {
                                    dynamic.onCollisionEntity(entity, v.overlapV.reverse());
                                    entity.onCollisionEntity(dynamic, v.overlapV.reverse());
                                }
                            }
                            break;
                        case definitions.COLLISION_TYPE.RECT: {
                                const v = dynamic.testCollisionRect(entity.collider);

                                if (v) {
                                    dynamic.onCollisionEntity(entity, v.overlapV);
                                    entity.onCollisionEntity(dynamic, v.overlapV);
                                }
                            }
                            break;
                    }
                }
            }

            // dynamic vs dynamic
            for (let j = 0, count = dynamicEntities.length; j < count; ++j) {
                const entity = dynamicEntities[j];

                if (entity.enabled && entity.visible) {
                    switch (entity.collisionType) {
                        case definitions.COLLISION_TYPE.CIRCLE: {
                                const v = dynamic.testCollisionCircle(entity.collider);
                                if (v) {
                                    dynamic.onCollisionEntity(entity, v.overlapV.reverse());
                                    entity.onCollisionEntity(dynamic, v.overlapV.reverse());
                                }
                            }
                            break;
                        case definitions.COLLISION_TYPE.RECT: {
                                const v = dynamic.testCollisionRect(entity.collider);

                                if (v) {
                                    dynamic.onCollisionEntity(entity, v.overlapV);
                                    entity.onCollisionEntity(dynamic, v.overlapV);
                                }
                            }
                            break;
                    }
                }
            }

            // query nearby tiles
            const size = 200;
            const tiles = this._tilemap.queryRect(new PIXI.Rectangle(
                dynamic.collisionX - size * 0.5,
                dynamic.collisionY - size * 0.5,
                size, size
            ));

            // dynamic vs tile
            for (let j = 0, count = tiles.length; j < count; ++j) {
                const tile = tiles[j];
                if (tile.collisionEnabled) {
                    for (let i = 0; i < tile.colliders.length; ++i) {
                        const collider = tile.colliders[i];
                        const v = dynamic.testCollisionRect(
                            collider
                        );
    
                        if (v) {
                            dynamic.onCollisionTile(tile, v.overlapV);
                            tile.onCollisionEntity(dynamic, v.overlapV);
                        }
                    }
                } else {
                    if (dynamic instanceof PlayerIvandoe) {
                        for (let i = 0; i < tiles.length; ++i) {
                            const tile = tiles[i];
    
                            if (dynamic.x >= tile.x && dynamic.x <= tile.x + tile.width && 
                                dynamic.y >= tile.y && dynamic.y <= tile.y + tile.height) {

                                const levelData = json(GameScene.active.loadedLevel);
                                for (let j = 0; j < levelData.footstepSFX.length; ++j) {
                                    let currentData = levelData.footstepSFX[j];
                                    if (tile.texture.textureCacheIds[0] && tile.texture.textureCacheIds[0].includes(currentData.suffix)) {
                                        dynamic.footstepSFXs = currentData.footsteps;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    sortEntities() {
        const entities = this._entities.filter((x) => x.zSort && x.visible);
        entities.sort((x, y) => {
            return x.z - y.z;
        });

        for (let i = 0, count = entities.length; i < count; ++i) {
            const entity = entities[i];
            this._gameLayer.view.addChild(entity);
        }
    }

    /**
     * @param {number} gid
     * @param {object} data
     * @param {boolean} flippedHorizontally
     * @param {boolean} flippedVertically
     * @returns {boolean}
     */
    filterEntity(gid, data, flippedHorizontally, flippedVertically) {
        return true;
    }

    /**
     * @param {string} decalName 
     * @param {Decal} decal 
     */
    _initDecalEffects(decalName, decal) {
        if (decalName.includes("campfire")) {
            decal.emitFx([texture("smoke_particle_fx_00"), texture("smoke_particle_fx_01")], "fx_smoke_particle_smoulder");
            decal.emitter.updateSpawnPos(0, -40);
        } 
        if (decalName.includes("bs_d_cauldron_00")) {
            decal.emitFx([texture("smoke_particle_fx_00"), texture("smoke_particle_fx_01")], "fx_smoke_particle_smoulder");
            decal.emitter.updateSpawnPos(0, -155);
        } 
    }

    /**
     * @param {object} tilemapData
     * @param {object} collisionData
     * @private
     */
    _loadEntities(tilemapData, collisionData) {
        const layerArray = P3.utils.objectToArray(tilemapData.layers);
        for (let i = 0, count = layerArray.length; i < count; ++i) {
            const layerData = layerArray[i];
            if (layerData.type === definitions.TILEMAP_LAYER_TYPE.OBJECT) {
                const objectArray = P3.utils.objectToArray(layerData.objects)
                for (let j = 0, count = objectArray.length; j < count; ++j) {
                    const objectData = objectArray[j];

                    const flippedHorizontally = (objectData.gid & definitions.TILESET_FLIPPED_HORIZONTALLY_FLAG) !== 0;
                    const flippedVertically = (objectData.gid & definitions.TILESET_FLIPPED_VERTICALLY_FLAG) !== 0;

                    const gid = objectData.gid &
                        ~(definitions.TILESET_FLIPPED_HORIZONTALLY_FLAG |
                        definitions.TILESET_FLIPPED_VERTICALLY_FLAG |
                        definitions.TILESET_FLIPPED_DIAGONALLY_FLAG |
                        definitions.TILESET_ROTATED_HEXAGONAL_120_FLAG);

                    if (!this.filterEntity(gid, objectData, flippedHorizontally, flippedVertically))
                        continue;

                    const decalTexture = this._tilemap.tileset.getTexture(gid);
                    
                    if (decalTexture) {
                        const zsort = Tilemap.getCustomProperty("zsort", objectData.properties);
                        const phase = Tilemap.getCustomProperty("phase", objectData.properties);

                        if (!phase || settings.PHASE == phase) {
                            const decal = new Decal(decalTexture, gid);
                            decal.position.set(objectData.x + objectData.width * 0.5, objectData.y);
                            decal.scale.x = flippedHorizontally ? -1 : 1;
                            decal.scale.y = flippedVertically ? -1 : 1;

                            let treasureFound = false;

                            if (objectData.properties && 
                                common.saveData.activeSlot.treasures.indexOf(GameScene.active.loadedLevel + "_" + gid) < 0) {
                                for (let k in objectData.properties) {
                                    if (objectData.properties[k].name == 'treasure') {
                                        if (common.saveData.activeSlot.purchases.item_compass) {
                                            treasureFound = true;
                                            //console.log('TREASURE DETECTED!');
                                        } else {
                                            decal.treasureReady = true;
                                        } 
                                    }
                                }
                            }

                            if (treasureFound) {
                                const treasureSpot = new HiddenTreasureSpot(decal);
                                treasureSpot.position.set(objectData.x + objectData.width * 0.5, objectData.y);
                                GameScene.active.world.addEntity(treasureSpot);
                                //console.log('TREASURE SPOT', treasureSpot);
                            }

                            if (zsort === false) {
                                decal.zSort = false;   
                            }
    
                            const name = decalTexture.textureCacheIds[0].split('.')[0];
                            const collision = collisionData[name];
    
                            this._initDecalEffects(name, decal);
    
                            if (collision && !treasureFound) {
                                decal.collisionEnabled = collision.radius > 0 || collision.width !== undefined;
                                decal.collisionRadius = collision.radius;
                                decal.collisionOffsetX = collision.offset.x;
                                decal.collisionOffsetY = collision.offset.y;
    
                                if(collision.radius === undefined) {
                                    decal.collisionSize = new PIXI.Point(collision.width, collision.height);
                                    decal.collisionType = definitions.COLLISION_TYPE.RECT;
                                } else {
                                    decal.collisionType = definitions.COLLISION_TYPE.CIRCLE;
                                }
                                
                                decal.initCollider(decal.x + collision.offset.x, decal.y + collision.offset.y);
                            }
    
                            let layer = definitions.WORLD_LAYER_TYPE.GAME;
                            if (Tilemap.getCustomProperty("shadow", layerData.properties)) {
                                decal.zSort = false;
                                layer = definitions.WORLD_LAYER_TYPE.SHADOW;
                            }
                            this.addEntity(decal, layer);
    
                            if (Tilemap.getCustomProperty("additive", layerData.properties)) {
                                decal.view.blendMode = PIXI.BLEND_MODES.ADD;
                            }
                        }
                    }
                    else
                    {
                        console.warn("[" + this.constructor.name + "]", "Texture (gid: '" + gid + "' not found in tileset");
                    }
                }
            }
        }
    }

    /**
     * @param {PIXI.Point} parallax
     * @returns {P3.CameraLayer}
     * @private
     */
    _addEmptyLayer(parallax) {
        const container = new PIXI.Container();
        this.addChild(container);

        const layer = new P3.CameraLayer(container, parallax);
        this._camera.addLayer(layer);

        return layer;
    }

    /**
     * @param {Entity} entity
     * @param {number} layer
     * @returns {boolean}
     */
    addEntity(entity, layer = definitions.WORLD_LAYER_TYPE.GAME) {
        if (this._entities.indexOf(entity) !== -1)
            return false;

        // initialize entity
        entity.world = this;
        entity.init();

        switch (layer) {
            case definitions.WORLD_LAYER_TYPE.GAME: {
                this._gameLayer.view.addChild(entity);
                break;
            }
            case definitions.WORLD_LAYER_TYPE.SHADOW: {
                this._shadowLayer.view.addChild(entity);
                break;
            }
        }

        this._entities.push(entity);
        return true;
    }

    /**
     * @param {Entity} entity
     * @param {boolean} [destroy=false]
     * @returns {boolean}
     */
    removeEntity(entity, destroy = false) {
        const i = this._entities.indexOf(entity);
        if (i === -1)
            return false;

        entity.parent.removeChild(entity);
        this._entities.splice(i, 1);
        destroy && entity.destroy();
        return true;
    }

    /**
     * @param {array<Entity>} [exclude=[]]
     */
    removeEntities(exclude = []) {
        for (let i = this._entities.length - 1; i >= 0; --i) {
            const entity = this._entities[i];
            if (exclude.indexOf(entity) === -1) {
                this.removeEntity(entity, true);
            }
        }
    }

    /**
     * @param {SAT.Circle} circle1
     * @param {SAT.Circle} circle2
     * @return {SAT.Response}
    */
    testCollisionCircle(circle1, circle2) {
        let response = new SAT.Response();
        let collided = SAT.testCircleCircle(circle1, circle2, response);

        if(collided) {
            return response;
        }
        
        return null;
    }

    /**
     * @param {SAT.Circle} circle1
     * @param {SAT.Circle} circle2
     * @return {P3.Vector2}
    */
    testCollisionCircleFast(circle1, circle2) {
        let response = new SAT.Response();
        let collided = SAT.testCircleCircle(circle1, circle2, response);

        if(collided) {
            return true;
        }
        
        return false;
    }

    /**
     * @param {SAT.Circle} circle 
     * @param {SAT.Box} rect 
     * @return {SAT.Response}
     */
    testCollisionRect(circle, rect) {
        let response = new SAT.Response();
        let collided = SAT.testPolygonCircle(rect, circle, response);

        if(collided) {
            return response;
        }
        
        return null;
    }

    /**
     * @param {SAT.Circle} circle
     * @param {SAT.Box} rect
     * @return {P3.Vector2}
    */
    testCollisionRectFast(circle, rect) {
        let response = new SAT.Response();
        let collided = SAT.testPolygonCircle(rect, circle, response);

        if(collided) {
            return true;
        }
        
        return false;
    }

    // testCollisionCircle(x1, y1, radius1, x2, y2, radius2) {
    //     radius1 = Math.max(0, radius1);
    //     radius2 = Math.max(0, radius2);

    //     if (radius1 + radius2 > 0) {
    //         const v = new P3.Vector2(x2 - x1, y2 - y1);
    //         const l = v.length;
    //         let r = radius1 + radius2;
    //         if (l < r) {
    //             v.x /= l;   // manual normalization to remove another sqrt
    //             v.y /= l;   // manual normalization to remove another sqrt
    //             v.scaleBy(Math.max(0, r - l) * -0.5);   // scale and flip
    //             //console.log(v);
    //             return v;
    //         }
    //     }
    //     return null;
    // }

    /**
     * old version of the testCollisionCircleFast function without SAT
     * @param {number} x1 
     * @param {number} y1 
     * @param {number} radius1 
     * @param {number} x2 
     * @param {number} y2 
     * @param {number} radius2 
     * @returns 
     */
    testCollisionCircleFastOld(x1, y1, radius1, x2, y2, radius2) {
        radius1 = Math.max(0, radius1);
        radius2 = Math.max(0, radius2);

        if (radius1 + radius2 > 0) {
            const v = new P3.Vector2(x2 - x1, y2 - y1);
            const l = v.lengthSq;
            let r = radius1 + radius2;
            r *= r;
            if (l < r) {
                return true;
            }
        }
        return false;
    }

    // testCollisionRect(x1, y1, radius, x2, y2, width, height) {
    //     if (width > 0 && height > 0) {
    //         const p = new PIXI.Point(x1, y1);
    //         p.x = Math.max(p.x, x2);
    //         p.x = Math.min(p.x, x2 + width);
    //         p.y = Math.max(p.y, y2);
    //         p.y = Math.min(p.y, y2 + height);

    //         if (((x1 - p.x) ** 2 + (y1 - p.y) ** 2) < radius ** 2) {
    //             const v = new P3.Vector2(x1 - p.x, y1 - p.y);
    //             const l = v.length;
    //             v.x /= l;
    //             v.y /= l;
    //             v.scaleBy(radius - l);
    //             return v;
    //         }
    //     }
        
    //     return null;
    // }

    /**
     * previous version of the testCollisionRectFastOld function without using SAT library
     * @param {number} x1 
     * @param {number} y1 
     * @param {number} radius 
     * @param {number} x2 
     * @param {number} y2 
     * @param {number} width 
     * @param {number} height 
     * @returns 
     */
    testCollisionRectFastOld(x1, y1, radius, x2, y2, width, height) {
        if (width > 0 && height > 0) {
            const p = new PIXI.Point(x1, y1);
            p.x = Math.max(p.x, x2);
            p.x = Math.min(p.x, x2 + width);
            p.y = Math.max(p.y, y2);
            p.y = Math.min(p.y, y2 + height);

            if (((x1 - p.x) ** 2 + (y1 - p.y) ** 2) < radius ** 2) {
                return true;
            }
        }
        return false;
    }

    /**
     * @param {PIXI.Point} center
     * @param {number} maxDistance
     * @param {number} [minDistance=0]
     * @param {number} [safeDistance=0]
     * @param {PIXI.Point[]} [blacklist=[]]
     * @param {number} [attempts=1]
     * @return {PIXI.Point}
     */
    findSafePosition(center, maxDistance, minDistance = 0, safeDistance = 0, blacklist = [], attempts = 1) {
        attempts = Math.max(1, attempts);

        loop:
        while (attempts-- > 0) {
            const theta = Math.random() * Math.PI * 2;
            const distance = random(minDistance, maxDistance);
            const position = new PIXI.Point(center.x + Math.cos(theta) * distance, center.y + Math.sin(theta) * distance);

            const tiles = this._tilemap.queryRect(new PIXI.Rectangle(position.x - 0.5, position.y - 0.5, 1, 1));
            for (let i = 0, count = tiles.length; i < count; ++i) {
                const tile = tiles[i];
                if (tile.collisionEnabled && this.testCollisionRectFastOld(
                    position.x,
                    position.y,
                    safeDistance,
                    tile.collisionX,
                    tile.collisionY,
                    tile.collisionWidth,
                    tile.collisionHeight)) {
                    continue loop;
                }
            }

            const entities = this._entities.filter((x) => !x.collisionDynamic && x.collisionEnabled);
            for (let i = 0, count = entities.length; i < count; ++i) {
                const entity = entities[i];
                if (this.testCollisionCircleFastOld(
                    position.x,
                    position.y,
                    safeDistance,
                    entity.collisionX,
                    entity.collisionY,
                    entity.collisionRadius)) {
                    continue loop;
                }
            }

            for (let i = 0, count = blacklist.length; i < count; ++i) {
                const to = new P3.Vector2(blacklist[i].x - position.x, blacklist[i].y - position.y);
                if (to.lengthSq < safeDistance ** 2) {
                    continue loop;
                }
            }
            return position;
        }
        return null;
    }

    addHiddenTreasureSpots() {
        for (let i in this._entities) {
            if (this._entities[i].treasureReady) {
                const treasureSpot = new HiddenTreasureSpot(this._entities[i]);
                treasureSpot.position.set(this._entities[i].x, this._entities[i].y);
                GameScene.active.world.addEntity(treasureSpot);
                console.log('NEW TREASURE SPOT', treasureSpot);
            }
        }
    }

    /**
     * @return Array
     */
    getHiddenTreasure() {
        const arr = [];
        for (let i in this._entities) {
            if (this._entities[i].isHiddenTreasureSpot) {
                arr.push(this._entities[i]);
            }
        }
        return arr;
    }

    /**
     * @returns {PIXI.Container}
     */
    get gameLayer() {
        return this._gameLayer;
    }

    /**
     * @returns {P3.Camera}
     */
    get camera() {
        return this._camera;
    }

    /**
     * @returns {Tilemap}
     */
    get tilemap() {
        return this._tilemap;
    }
}